import { useMemo, useState } from 'react'
import { legacyMenus, legacySidebarRecentResults, type LegacyMenuGroup } from '../../mock/legacy'
import './LegacySidebar.css'

type LegacySidebarProps = {
  collapsed: boolean
  onCollapsedChange: (collapsed: boolean) => void
  onNavigate: (path: string) => void
  pathname: string
}

const menuIcons: Record<string, string> = {
  '고객 관리': 'icon-user-group-02',
  스케줄: 'icon-calendar-03',
  '문자 발송': 'icon-chat',
  '통계 / 현황': 'icon-analytics-up',
  'EDRP 사용료 관리': 'icon-coins-dollar',
  설정: 'icon-setting-06',
  시스템관리: 'icon-server-stack-01',
  테스트: 'icon-test-tube-01',
}

function SidebarIcon({ name, className = '' }: { name: string; className?: string }) {
  return <svg className={`sidebar-svg-icon ${className}`.trim()} aria-hidden="true"><use href={`/legacy/common/img/icons-sprite.svg#${name}`} /></svg>
}

export function LegacySidebar({ collapsed, onCollapsedChange, onNavigate, pathname }: LegacySidebarProps) {
  const [openMenu, setOpenMenu] = useState(() => legacyMenus.find((menu) => menu.items.some((item) => item.path.split('?')[0] === pathname))?.label ?? '')
  const [recentTab, setRecentTab] = useState<'member' | 'contract'>('member')
  const activeGroup = useMemo(() => legacyMenus.find((menu) => menu.items.some((item) => item.path.split('?')[0] === pathname))?.label, [pathname])

  const openGroup = (group: LegacyMenuGroup) => {
    setOpenMenu((current) => current === group.label ? '' : group.label)
  }

  return <aside className={`g_v1_side_menu_wrap legacy-v2-sidebar ${collapsed ? 'close_navi' : ''}`} id="g_top_menu">
    <div className="article_wrap">
      <article className="g_logo_article">
        <button className="g_logo_button" type="button" aria-label="일 스케줄로 이동" onClick={() => onNavigate('/v1/schedule/daily/')}>
          <span className="g_logo_viewport"><img className="g_logo_img" src="/legacy/common/img/logo-studioflow.svg" alt="Studioflow" /></span>
        </button>
      </article>

      <article className="g_account_article" aria-label="로그인 계정 정보">
        <button className="sidebar-account-heading sidebar-disclosure-toggle" type="button">
          <strong className="sidebar-account-name">관리자</strong><span className="sidebar-account-role">총관리자</span><span className="sidebar-disclosure-spacer" /><SidebarIcon name="icon-caret-down" className="sidebar-disclosure-icon" />
        </button>
        <form className="sidebar-logout-form"><button className="sidebar-logout-button" type="button"><SidebarIcon name="icon-logout-01" className="sidebar-inline-icon" /><span>로그아웃</span></button></form>
      </article>

      <article className="g_menu_article">
        <ul className="main_menu">
          {legacyMenus.map((group) => {
            const expanded = openMenu === group.label
            const sourceItems = group.label === '고객 관리'
              ? group.items.filter((item) => item.path !== '/v1/customer_history/')
              : group.label === 'EDRP 사용료 관리'
                ? group.items.slice(0, 2)
                : group.items
            return <li className={`click_navi_event sidebar-menu-item ${expanded ? 'sidebar-menu-open ' : ''}${activeGroup === group.label ? 'sidebar-active' : ''}`} key={group.label}>
              <button className="sidebar-menu-trigger" type="button" onClick={() => openGroup(group)} aria-expanded={expanded}>
                <SidebarIcon name={menuIcons[group.label]} className="main_icon_img" />
                <span className={`navi_txt ${group.label === '문자 발송' ? 'msg' : ''}`}>{group.label}</span>
              </button>
              {expanded && <ul className="sub_menu">
                {sourceItems.map((item) => <li className="sub_menu_item" key={`${item.path}-${item.label}`}>
                  <button className={pathname === item.path.split('?')[0] ? 'is-current' : ''} type="button" onClick={() => onNavigate(item.path)}>{item.label}</button>
                </li>)}
                {group.label === '설정' && <li className="sub_menu_item none_access"><span>공지사항/문의게시판(준비 중)</span></li>}
              </ul>}
            </li>
          })}
          <li className={`click_navi_event sidebar-menu-item ${openMenu === '테스트' ? 'sidebar-menu-open' : ''}`}><button className="sidebar-menu-trigger" type="button" onClick={() => setOpenMenu((current) => current === '테스트' ? '' : '테스트')} aria-expanded={openMenu === '테스트'}><SidebarIcon name={menuIcons.테스트} className="main_icon_img" /><span className="navi_txt">테스트</span></button>{openMenu === '테스트' && <ul className="sub_menu"><li className="sub_menu_item none_access"><span>통계 구분 관리</span></li></ul>}</li>
        </ul>
      </article>

      <article className="g_history_article">
        <div className="navi_bottom_layout close_dom"><div className="g_navi_list_wrap">
          <section className="sidebar-shortcut-section sidebar-favorites-section"><button className="g_navi_title sidebar-shortcut-toggle" type="button"><SidebarIcon name="icon-caret-down" />즐겨찾기</button></section>
          <section className="sidebar-shortcut-section sidebar-history-section"><button className="g_navi_title sidebar-shortcut-toggle" type="button"><SidebarIcon name="icon-caret-down" />최근 조회</button></section>
        </div></div>
      </article>

      <article className="g_cust_search_article">
        <section className="cust_search_header">
          <div className="search_tab"><button className={`search_tab_btn tab-link ${recentTab === 'member' ? 'current_list' : ''}`} type="button" onClick={() => setRecentTab('member')}>최근 회원 목록</button></div>
          <div className="search_tab"><button className={`search_tab_btn tab-link ${recentTab === 'contract' ? 'current_list' : ''}`} type="button" onClick={() => setRecentTab('contract')}>최근 계약 목록</button></div>
        </section>
        <section className="cust_search_main">
          <div className="cust_search_form"><input className="g_input" aria-label="최근 회원 및 계약 검색" /><button className="g_btn g_btn_submit search size_1" type="button" aria-label="통합검색"><SidebarIcon name="icon-search-01" /></button></div>
          <button className="sidebar-search-sort" type="button">최신순 <SidebarIcon name="icon-caret-down" /></button>
          <ul className="cust_search_list">
            {legacySidebarRecentResults.map((customer) => <li className="search_list_item" key={customer.no}>
              <div className="sidebar-result-top"><span className="sidebar-result-no">{customer.no}</span><span>{customer.phone}</span></div>
              <div className="sidebar-result-row"><strong>{recentTab === 'member' ? customer.parent : customer.baby}</strong><span>{customer.phone}</span></div>
              <div className="sidebar-result-row sidebar-result-baby">(baby){customer.baby}</div>
            </li>)}
          </ul>
        </section>
      </article>
    </div>

    <div className="absolute_wrap"><button className="menu_toggle_btn" id="g_navi_toggle" type="button" aria-label="메뉴 접기" onClick={() => onCollapsedChange(!collapsed)}><SidebarIcon name={collapsed ? 'icon-chevron-right' : 'icon-chevron-left'} /></button></div>
  </aside>
}
